import { Link } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import { ArrowUpRight, Gauge, Compass, Wallet, Sparkles, Target, LineChart } from "lucide-react";

const PILLARS = [
  { icon: Compass, title: "Personal Stability", weight: "35%", color: "#DDA15E", desc: "Stress, routines, resilience, support, energy, and time capacity." },
  { icon: Gauge, title: "Professional Momentum", weight: "35%", color: "#FF8A00", desc: "Employment stability, skill alignment, network, and career growth." },
  { icon: Wallet, title: "Financial Health", weight: "30%", color: "#5C6B4E", desc: "Cash flow, savings cushion, debt, discipline, and risk capacity." },
];

const BANDS = [
  { name: "Sinking", range: "0–40", color: "#8A817C", note: "Steady the ground before any major move." },
  { name: "Floating", range: "41–65", color: "#DDA15E", note: "Holding steady — prepare to raise your odds." },
  { name: "Advancing", range: "66–85", color: "#FF8A00", note: "Solid foundation. Proceed with intention." },
  { name: "Compounding", range: "86–100", color: "#FF4D4D", note: "Positioned to move decisively." },
];

const STEPS = [
  { icon: Sparkles, title: "Assess", desc: "A 3–5 minute survey across personal, professional & financial life." },
  { icon: Gauge, title: "Score", desc: "A warm 0–100 Scorecaster Score plus three sub-scores." },
  { icon: Target, title: "Forecast", desc: "A Decision Fit Score for the specific move you're weighing." },
  { icon: LineChart, title: "Advise", desc: "An AI action plan to raise your odds over 30–90 days." },
];

const HERO_IMG = "https://images.unsplash.com/photo-1617720197345-5e5235ec6220?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function Landing() {
  const { user } = useAuth();
  const cta = user ? "/survey" : "/auth?mode=register";

  return (
    <div className="min-h-screen grain">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full opacity-40 blur-[120px]" style={{ background: "radial-gradient(circle, #FFDCA8, transparent 70%)" }} />
        <div className="relative max-w-7xl mx-auto px-5 md:px-10 pt-16 md:pt-24 pb-20">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6 fade-up">
              <span className="label-tiny">Agentic decision intelligence</span>
              <h1 className="font-display font-extrabold tracking-tight leading-[1.02] mt-5 text-4xl sm:text-5xl lg:text-6xl text-ink">
                Score your current situation.{" "}
                <span style={{ background: "linear-gradient(90deg, #FF4D4D, #FF8A00)", WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent", color: "transparent" }}>
                  Forecast
                </span>{" "}
                your next move.
              </h1>
              <p className="mt-6 text-stone text-base md:text-lg max-w-xl leading-relaxed">
                Scorecaster turns your present personal, professional, and financial position into a
                readiness score — then forecasts how prepared you are for the decisions that matter most.
              </p>
              <div className="mt-9 flex flex-wrap items-center gap-4">
                <Link to={cta} data-testid="hero-cta" className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-brand-red text-white font-semibold hover:bg-[#e63f3f] transition-colors duration-300">
                  Take the survey
                  <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
                </Link>
                <span className="font-mono text-xs text-stone">~4 min · 22 questions</span>
              </div>
              <p className="mt-8 font-display font-semibold text-sm tracking-[0.3em] uppercase text-stone/70">
                Scoring to the future
              </p>
            </div>

            <div className="lg:col-span-6 fade-up" style={{ animationDelay: "0.15s" }}>
              <div className="relative">
                <img src={HERO_IMG} alt="Person planning outdoors in golden light" className="w-full h-[340px] md:h-[420px] object-cover rounded-3xl border border-clay shadow-[0_8px_32px_rgba(140,130,120,0.12)]" />
                <div className="absolute -bottom-6 -left-2 sm:left-6 card-soft p-5 w-[220px]">
                  <div className="flex items-center gap-2 mb-1">
                    <img src="/scorecaster-logo.png" alt="" className="h-9 w-auto" />
                    <span className="label-tiny">Core score</span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="font-display text-4xl font-extrabold tabular-nums text-brand-orange">74</span>
                    <span className="text-sm text-stone">Advancing</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-16 mt-6">
        <span className="label-tiny">The workflow</span>
        <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight mt-3 mb-10 text-ink">Assess. Score. Forecast. Advise.</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {STEPS.map((s, i) => (
            <div key={s.title} className="card-soft p-6 hover:-translate-y-1 transition-transform duration-500">
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 rounded-full bg-brand-orange/10 flex items-center justify-center">
                  <s.icon size={20} className="text-brand-orange" />
                </div>
                <span className="font-mono text-xs text-stone/60">0{i + 1}</span>
              </div>
              <h3 className="font-display font-bold mt-5 mb-2 text-ink">{s.title}</h3>
              <p className="text-sm text-stone leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PILLARS */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-16">
        <span className="label-tiny">Three pillars</span>
        <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight mt-3 mb-10 text-ink">What your score is built from</h2>
        <div className="grid md:grid-cols-3 gap-5">
          {PILLARS.map((p) => (
            <div key={p.title} className="card-soft p-7">
              <div className="flex items-center justify-between">
                <div className="w-11 h-11 rounded-2xl flex items-center justify-center" style={{ background: `${p.color}18` }}>
                  <p.icon size={22} style={{ color: p.color }} />
                </div>
                <span className="font-mono text-sm text-stone">{p.weight}</span>
              </div>
              <h3 className="font-display font-bold mt-5 text-ink">{p.title}</h3>
              <p className="text-sm text-stone mt-2 leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SCORE BANDS */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-16">
        <span className="label-tiny">What your number means</span>
        <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight mt-3 mb-10 text-ink">Four readiness bands</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {BANDS.map((b) => (
            <div key={b.name} className="card-soft p-6 relative overflow-hidden">
              <div className="absolute top-0 left-0 h-full w-1.5" style={{ background: b.color }} />
              <span className="font-mono text-2xl font-bold tabular-nums" style={{ color: b.color }}>{b.range}</span>
              <h3 className="font-display font-bold mt-3 text-ink">{b.name}</h3>
              <p className="text-sm text-stone mt-2 leading-relaxed">{b.note}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-20">
        <div className="rounded-3xl border border-clay bg-gradient-to-br from-[#FFF3E4] to-[#FDFBF7] p-10 md:p-16 text-center shadow-[0_8px_32px_rgba(140,130,120,0.08)]">
          <h2 className="font-display font-extrabold tracking-tight text-3xl md:text-4xl text-ink">Know your number. Shape your next move.</h2>
          <p className="text-stone mt-4 max-w-lg mx-auto">
            Not a prediction of the future — a clear, calm read on your readiness today, and what to nurture first.
          </p>
          <Link to={cta} data-testid="cta-bottom" className="inline-flex items-center gap-2 mt-8 px-8 py-3.5 rounded-full bg-brand-red text-white font-semibold hover:bg-[#e63f3f] transition-colors duration-300">
            Start free assessment <ArrowUpRight size={18} />
          </Link>
        </div>
      </section>

      <footer className="border-t border-clay py-8 text-center text-xs text-stone font-mono">
        Scorecaster — measure today, decide tomorrow.
      </footer>
    </div>
  );
}
